import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Shield } from "lucide-react";
import axios from "axios";

// Use current domain for API calls (works with custom domains)
const API_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? `${window.location.origin}/api`
  : `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_bc07a5b0-4401-454c-9821-20d81dadd6da/artifacts/21c8lw1c_fa793a21-0229-4214-a16b-e1c0df8a86d3.png";

const Register = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [showAdminField, setShowAdminField] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Lösenorden matchar inte");
      return;
    }

    if (password.length < 6) {
      toast.error("Lösenordet måste vara minst 6 tecken");
      return;
    }

    setLoading(true);

    try {
      // If admin code is provided, use admin-setup endpoint
      if (adminCode) {
        const response = await axios.post(`${API_URL}/auth/admin-setup`, {
          email,
          name,
          password,
          setup_key: adminCode
        });
        // Store token and user data
        localStorage.setItem("token", response.data.access_token);
        toast.success("Admin-konto skapat!");
        window.location.href = "/dashboard";
        return;
      }
      
      await register(name, email, password);
      toast.success("Konto skapat!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registreringen misslyckades");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Logo */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-[#2a2a2a] items-center justify-center"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/40" />
        <div className="relative z-10 flex flex-col items-center justify-center p-12">
          <img 
            src={LOGO_URL} 
            alt="Mälair Installation AB" 
            className="w-80 h-auto mb-8"
          />
          <p className="text-lg text-white/70 text-center max-w-md">
            Skapa konto för att börja rapportera tid
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#0d1218]">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Mälair" 
              className="w-48 h-auto"
            />
          </div>

          <Card className="border-0 shadow-xl bg-[#2a2a2a] border-[#3a3a3a]">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle
                className="text-2xl font-bold text-white"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Skapa konto
              </CardTitle>
              <CardDescription className="text-white/25">Fyll i dina uppgifter för att komma igång</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white/40">Namn</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Ditt namn"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 bg-[#0d1218] border-[#3a3a3a] text-white placeholder:text-white/40"
                      data-testid="register-name"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/40">E-post</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="namn@foretag.se"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-[#0d1218] border-[#3a3a3a] text-white placeholder:text-white/40"
                      data-testid="register-email"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/40">Lösenord</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Minst 6 tecken"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-[#0d1218] border-[#3a3a3a] text-white placeholder:text-white/40"
                      data-testid="register-password"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-white/40">Bekräfta lösenord</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Skriv lösenordet igen"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 bg-[#0d1218] border-[#3a3a3a] text-white placeholder:text-white/40"
                      data-testid="register-confirm-password"
                      required
                    />
                  </div>
                </div>
                
                {/* Admin code toggle */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAdminField(!showAdminField)}
                    className="text-xs text-white/40 hover:text-white/25 flex items-center gap-1"
                  >
                    <Shield className="w-3 h-3" />
                    {showAdminField ? "Dölj admin-kod" : "Har du en admin-kod?"}
                  </button>
                </div>
                
                {showAdminField && (
                  <div className="space-y-2">
                    <Label htmlFor="adminCode" className="text-white/40">Admin-kod (valfritt)</Label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <Input
                        id="adminCode"
                        type="password"
                        placeholder="Ange admin-kod"
                        value={adminCode}
                        onChange={(e) => setAdminCode(e.target.value)}
                        className="pl-10 bg-[#0d1218] border-[#3a3a3a] text-white placeholder:text-white/40"
                        data-testid="register-admin-code"
                      />
                    </div>
                    <p className="text-xs text-white/40">Om du har en admin-kod blir ditt konto automatiskt admin</p>
                  </div>
                )}
                
                <Button
                  type="submit"
                  className="w-full btn-hover gap-2 bg-[#6dbf4a] hover:bg-[#5da83d] text-white"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? "Skapar konto..." : "Skapa konto"}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-white/25">
                  Har du redan ett konto?{" "}
                  <Link
                    to="/login"
                    className="font-medium text-[#6dbf4a] hover:underline"
                    data-testid="login-link"
                  >
                    Logga in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;
